/**
 * [Insider+] Iframe JWT Token Communication Utilities
 *
 * Sends JWT tokens from this parent window to the embedded Insider+ iframe.
 */

import { isIframeOriginAllowed, getTargetOrigin } from '../security/config';

/**
 * Send JWT token to iframe
 * @param iframeRef - Reference to the iframe element
 * @param jwtToken - The JWT token to send
 * @param targetOrigin - The origin of the iframe
 */
export const sendJwtTokenToIframe = (
  iframeRef,
  jwtToken,
  targetOrigin = getTargetOrigin(),
) => {
  if (!iframeRef?.contentWindow || !jwtToken) return;

  try {
    iframeRef.contentWindow.postMessage(
      { type: 'SET_JWT_TOKEN', payload: { jwtToken } },
      targetOrigin,
    );
  } catch (error) {
    // Silently fail - iframe may not be ready
  }
};

/**
 * [Insider+] Tell the embedded Insider+ iframe to clear its session on logout.
 *
 * The child listens for a PARENT_LOGOUT message and responds by signing out of
 * Supabase and resetting its auth store. Without this, a parent logout never
 * reaches the iframe, so the child reuses its persisted Supabase session on the
 * next login and skips re-authentication (stale session across logout).
 *
 * Posted with the Insider+ origin as targetOrigin, so only the Insider+ frame
 * receives it — other frames ignore a mismatched targetOrigin. Broadcasts to
 * every iframe in the DOM so the caller (the logout saga) needs no ref to the
 * specific frame; runs synchronously before the logout redirect so the frame
 * is still mounted when the message is delivered.
 */
export const broadcastParentLogoutToInsiderPlus = (
  targetOrigin = getTargetOrigin(),
) => {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('iframe').forEach((frame) => {
    try {
      const frameWindow = frame.contentWindow;
      if (frameWindow) {
        frameWindow.postMessage({ type: 'PARENT_LOGOUT' }, targetOrigin);
      }
    } catch {
      // Detached or inaccessible frame — ignore.
    }
  });
};

/**
 * Send the GBI API base URL to the iframe so it knows which backend to call.
 * The iframe whitelists allowed URLs; an unknown URL will be rejected on the
 * other side.
 */
export const sendApiBaseUrlToIframe = (
  iframeRef,
  apiBaseUrl,
  targetOrigin = getTargetOrigin(),
) => {
  if (!iframeRef?.contentWindow || !apiBaseUrl) return;

  try {
    iframeRef.contentWindow.postMessage(
      { type: 'SET_API_BASE_URL', payload: { apiBaseUrl } },
      targetOrigin,
    );
  } catch (error) {
    // Silently fail - iframe may not be ready
  }
};

const generateNonce = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// Module-level reference to the currently mounted Insider+ iframe.
// Allows non-React callers (e.g. the logout saga) to post messages without
// holding a React ref. Safe at module scope because only one Insider+ iframe
// is mounted at a time — same assumption as currentSendGeneration below.
let logoutIframe = null; // { element: HTMLIFrameElement, targetOrigin: string }

/**
 * Register the active Insider+ iframe so sendLogoutToIframe() can reach it.
 * Call from the iframe's onLoad handler.
 */
export const registerIframeForLogout = (iframeElement, targetOrigin) => {
  logoutIframe = { element: iframeElement, targetOrigin };
};

/** Deregister on unmount so stale references don't linger. */
export const unregisterIframeForLogout = () => {
  logoutIframe = null;
};

/**
 * Fire-and-forget PARENT_LOGOUT message to the Insider+ iframe.
 * The child should call supabase.auth.signOut() on receipt.
 * No ack is expected — the parent navigates away immediately after.
 */
export const sendLogoutToIframe = () => {
  if (!logoutIframe?.element?.contentWindow) return;
  try {
    logoutIframe.element.contentWindow.postMessage(
      { type: 'PARENT_LOGOUT' },
      logoutIframe.targetOrigin,
    );
  } catch {
    // Silently fail — iframe may already be navigating away
  }
};

// Module-level generation counter. Each sendSsoTokenToIframe call bumps it;
// in-flight retry chains check the counter before posting again and bail if
// a newer send has started. Prevents an old chain's setTimeout from firing
// after a fresh token has been pushed — without this, the child's listener
// (which writes whichever SET_SSO_TOKEN arrives last) could end up with a
// stale token even though the parent's nonce check accepted the new one.
//
// Safe at module scope because the app only mounts one Insider+ iframe at a
// time. If we ever ran multiple iframes in parallel, scope this per
// iframeRef instead.
let currentSendGeneration = 0;

/**
 * Send SSO token to iframe with ack-based delivery confirmation.
 * After each send, waits for an SSO_TOKEN_ACK message from the iframe.
 * If no ack arrives within `delay` ms, retries up to `retries` more times.
 *
 * Each send embeds a one-time `nonce`. The child must echo it back in
 * SSO_TOKEN_ACK; acks with a missing or mismatched nonce are ignored. This
 * prevents an older send's ack from satisfying a newer send.
 *
 * The send is also tied to a module-level generation counter. A newer send
 * supersedes any in-flight retry chain — old chains stop posting on their
 * next scheduled attempt so they can't clobber the freshly pushed token.
 *
 * Protocol:
 *   parent → child  { type: 'SET_SSO_TOKEN', payload: { ssoToken, nonce } }
 *   child  → parent { type: 'SSO_TOKEN_ACK', nonce }
 *
 * @param iframeRef - Reference to the iframe element
 * @param ssoToken - The SSO token to send
 * @param targetOrigin - The origin of the iframe
 * @param retries - Number of retry attempts after the initial send (default 3)
 * @param delay - Ms to wait for ack before retrying (default 1000)
 */
export const sendSsoTokenToIframe = (
  iframeRef,
  ssoToken,
  targetOrigin = getTargetOrigin(),
  retries = 3,
  delay = 1000,
) => {
  if (!iframeRef?.contentWindow || !ssoToken) return false;

  currentSendGeneration += 1;
  const myGeneration = currentSendGeneration;

  const attempt = (remaining) => {
    // Bail if a newer sendSsoTokenToIframe call superseded us — the child
    // already has a fresher token; don't clobber it with this stale one.
    if (myGeneration !== currentSendGeneration) return;

    const nonce = generateNonce();

    try {
      iframeRef.contentWindow.postMessage(
        { type: 'SET_SSO_TOKEN', payload: { ssoToken, nonce } },
        targetOrigin,
      );
    } catch {
      // postMessage threw (e.g. cross-origin policy violation) — no point retrying
      return;
    }

    if (remaining <= 0) return;

    // Wait for the ack matching this attempt's nonce; retry if none arrives
    // within `delay` ms.
    let ackReceived = false;

    const ackHandler = (event) => {
      const originMatches =
        targetOrigin === '*'
          ? isIframeOriginAllowed(event.origin)
          : event.origin === targetOrigin;

      if (
        originMatches &&
        event.data?.type === 'SSO_TOKEN_ACK' &&
        event.data?.nonce === nonce
      ) {
        ackReceived = true;
        window.removeEventListener('message', ackHandler);
        clearTimeout(retryTimer);
      }
    };

    window.addEventListener('message', ackHandler);

    const retryTimer = setTimeout(() => {
      window.removeEventListener('message', ackHandler);
      if (!ackReceived && myGeneration === currentSendGeneration) {
        attempt(remaining - 1);
      }
    }, delay);
  };

  attempt(retries);
  return true;
};

/**
 * Setup message listener to respond to token requests from iframe.
 * Stores pending requests so tokens can be sent once available.
 * Pending requests time out after MAX_PENDING_POLLS * 500ms (15 seconds).
 *
 * For SSO specifically, the caller should pass `sendSsoToken` — a callback
 * that pushes the current token through sendSsoTokenToIframe (which adds
 * nonce + generation tracking). This ensures child-initiated
 * REQUEST_SSO_TOKEN responses get the same supersession + delivery
 * confirmation guarantees as parent-initiated pushes. Without it, a
 * child-driven response could carry a stale token and clobber a freshly
 * pushed one.
 *
 * @param jwtTokenGetter - Function that returns the current JWT token
 * @param ssoTokenGetter - Function that returns the current SSO token (used
 *   only to check "do we have one yet?" — actual send goes via sendSsoToken)
 * @param onAuthFailure - Optional callback invoked when the iframe reports
 *   AUTH_FAILED or AUTH_ERROR
 * @param onSubscriptionSuccess - Optional callback for SUBSCRIPTION_SUCCESS
 * @param apiBaseUrlGetter - Function that returns the API base URL
 * @param sendSsoToken - Optional () => boolean; pushes current token
 *   through the nonce + generation pipeline. Returns true if it sent.
 *   If omitted, the listener falls back to inline postMessage (no nonce,
 *   no supersession guard).
 * @returns Cleanup function to remove the event listener
 */
export const setupIframeTokenListener = (
  jwtTokenGetter,
  ssoTokenGetter = null,
  onAuthFailure = null,
  onSubscriptionSuccess = null,
  apiBaseUrlGetter = null,
  sendSsoToken = null,
) => {
  // Track pending requests from iframe so we can fulfill them when tokens arrive
  const pendingRequests = { jwt: null, sso: null, apiBaseUrl: null };

  let intervalId = null;
  let pendingPollCount = 0;
  const MAX_PENDING_POLLS = 30; // give up after 15 seconds (30 × 500ms)

  const stopPolling = () => {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
    pendingPollCount = 0;
  };

  const hasPending = () =>
    Boolean(
      pendingRequests.sso || pendingRequests.jwt || pendingRequests.apiBaseUrl,
    );

  const trySendPending = () => {
    if (!hasPending()) {
      stopPolling();
      return;
    }

    pendingPollCount += 1;
    if (pendingPollCount > MAX_PENDING_POLLS) {
      pendingRequests.sso = null;
      pendingRequests.jwt = null;
      pendingRequests.apiBaseUrl = null;
      stopPolling();
      return;
    }

    if (pendingRequests.sso) {
      if (sendSsoToken) {
        // Route through the nonce + generation pipeline.
        if (sendSsoToken()) {
          pendingRequests.sso = null;
        }
      } else {
        const ssoToken = ssoTokenGetter?.();
        if (ssoToken) {
          const { source, origin } = pendingRequests.sso;
          if (source) {
            try {
              source.postMessage(
                { type: 'SET_SSO_TOKEN', payload: { ssoToken } },
                origin,
              );
              pendingRequests.sso = null;
            } catch (e) {
              // source window may have been closed/navigated
            }
          }
        }
      }
    }

    if (pendingRequests.jwt) {
      const jwtToken = jwtTokenGetter();
      if (jwtToken) {
        const { source, origin } = pendingRequests.jwt;
        if (source) {
          try {
            source.postMessage(
              { type: 'SET_JWT_TOKEN', payload: { jwtToken } },
              origin,
            );
            pendingRequests.jwt = null;
          } catch (e) {
            // source window may have been closed/navigated
          }
        }
      }
    }

    if (pendingRequests.apiBaseUrl) {
      const apiBaseUrl = apiBaseUrlGetter?.();
      if (apiBaseUrl) {
        const { source, origin } = pendingRequests.apiBaseUrl;
        if (source) {
          try {
            source.postMessage(
              { type: 'SET_API_BASE_URL', payload: { apiBaseUrl } },
              origin,
            );
            pendingRequests.apiBaseUrl = null;
          } catch (e) {
            // source window may have been closed/navigated
          }
        }
      }
    }

    if (!hasPending()) {
      stopPolling();
    }
  };

  const ensurePolling = () => {
    if (intervalId === null) {
      pendingPollCount = 0;
      intervalId = setInterval(trySendPending, 500);
    }
  };

  const handleMessage = (event) => {
    // Security: Validate origin FIRST
    if (!isIframeOriginAllowed(event.origin)) {
      return;
    }

    if (event.data?.type === 'REQUEST_JWT_TOKEN') {
      const jwtToken = jwtTokenGetter();
      if (jwtToken && event.source) {
        event.source.postMessage(
          { type: 'SET_JWT_TOKEN', payload: { jwtToken } },
          event.origin,
        );
      } else {
        // Token not ready yet - store the request to fulfill later
        pendingRequests.jwt = { source: event.source, origin: event.origin };
        ensurePolling();
      }
    }

    // The child's AuthError.tsx posts AUTH_ERROR before window.location.reload(),
    // so it's largely self-healing — but mapping it to the same recovery path
    // means a transient backend hiccup gets caught before the iframe blows
    // away its in-flight state.
    if (
      event.data?.type === 'AUTH_FAILED' ||
      event.data?.type === 'AUTH_ERROR'
    ) {
      if (onAuthFailure) onAuthFailure();
    }

    if (event.data?.type === 'SUBSCRIPTION_SUCCESS') {
      if (onSubscriptionSuccess) onSubscriptionSuccess();
    }

    if (event.data?.type === 'REQUEST_SSO_TOKEN') {
      if (sendSsoToken) {
        // Route through the nonce + generation pipeline so the child gets
        // the same supersession guard as a parent-driven push.
        if (!sendSsoToken()) {
          // Token not ready yet — enqueue; the source/origin sentinel is
          // unused in this path since sendSsoTokenToIframe targets the
          // hook's iframeRef directly.
          pendingRequests.sso = { source: event.source, origin: event.origin };
          ensurePolling();
        }
      } else {
        const ssoToken = ssoTokenGetter?.();
        if (ssoToken && event.source) {
          event.source.postMessage(
            { type: 'SET_SSO_TOKEN', payload: { ssoToken } },
            event.origin,
          );
        } else {
          // Token not ready yet - store the request to fulfill later
          pendingRequests.sso = { source: event.source, origin: event.origin };
          ensurePolling();
        }
      }
    }

    if (event.data?.type === 'REQUEST_API_BASE_URL') {
      const apiBaseUrl = apiBaseUrlGetter?.();
      if (apiBaseUrl && event.source) {
        event.source.postMessage(
          { type: 'SET_API_BASE_URL', payload: { apiBaseUrl } },
          event.origin,
        );
      } else {
        pendingRequests.apiBaseUrl = {
          source: event.source,
          origin: event.origin,
        };
        ensurePolling();
      }
    }
  };

  window.addEventListener('message', handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
    stopPolling();
  };
};
